// Compatibility rules for component combinations

export const COMPATIBILITY_RULES = {
  // Hardware compatibility
  'gpu': ['deep-learning', 'cnn', 'transformer', 'computer-vision', 'llm'],
  'cpu': ['all', 'classical-ml', 'rule-based', 'lightweight'],
  'tpu': ['neural-networks', 'transformer', 'cnn', 'large-scale'],
  
  // Data compatibility
  'streaming': ['real-time', 'stream-processing', 'kafka', 'websocket'],
  'batch': ['etl', 'offline', 'warehouse', 'historical'],
  'vector': ['rag', 'semantic-search', 'embeddings', 'llm'],
  'relational': ['structured', 'sql', 'transactional', 'tabular'],
  'document': ['semi-structured', 'json', 'flexible-schema'],
  'graph': ['relationships', 'knowledge-graph', 'recommendation'],
  'time-series': ['iot', 'monitoring', 'temporal', 'metrics'],
  
  // Model compatibility
  'cnn': ['computer-vision', 'images', 'spatial', 'gpu'],
  'transformer': ['nlp', 'llm', 'sequence', 'attention'],
  'rnn': ['time-series', 'sequence', 'temporal'],
  'reinforcement': ['games', 'robotics', 'control', 'simulation'],
  'classical-ml': ['tabular', 'structured', 'traditional'],
  'ensemble': ['boosting', 'bagging', 'stacking'],
  
  // Interface compatibility
  'chatbot': ['llm', 'conversational', 'nlp', 'customer-service'],
  'dashboard': ['visualization', 'monitoring', 'analytics', 'bi'],
  'api': ['integration', 'programmatic', 'automation'],
  'mobile': ['edge', 'lightweight', 'on-device'],
  'web': ['browser', 'responsive', 'spa'],
  
  // Deployment compatibility
  'cloud': ['aws', 'azure', 'gcp', 'managed'],
  'on-premise': ['self-hosted', 'private', 'secure'],
  'edge': ['iot', 'mobile', 'embedded', 'low-latency'],
  'hybrid': ['cloud', 'on-premise', 'flexible'],
  
  // Industry compatibility
  'healthcare': ['hipaa', 'medical', 'clinical', 'privacy'],
  'finance': ['banking', 'trading', 'risk', 'compliance'],
  'retail': ['ecommerce', 'recommendation', 'inventory'],
  'manufacturing': ['industrial', 'quality-control', 'predictive-maintenance'],
  'autonomous': ['robotics', 'vehicles', 'drones', 'navigation']
};

export const checkCompatibility = (component1, component2) => {
  if (!component1 || !component2) return true;
  if (component1.id === component2.id) return false;
  
  const tags1 = new Set(component1.tags || []);
  const tags2 = new Set(component2.tags || []);
  
  // Check if any tags overlap
  for (const tag of tags1) {
    if (tags2.has(tag)) return true;
  }
  
  // Check compatibility rules
  for (const [key, values] of Object.entries(COMPATIBILITY_RULES)) {
    if (tags1.has(key) && values.some(v => tags2.has(v))) return true;
    if (tags2.has(key) && values.some(v => tags1.has(v))) return true;
  }
  
  // Default to compatible if no conflicts found
  return true;
};

export const getCompatibleComponents = (selectedComponents, allComponents) => {
  if (selectedComponents.length === 0) return allComponents;
  
  return allComponents.filter(comp => {
    if (selectedComponents.some(sc => sc.id === comp.id)) return false;
    
    // Check compatibility with all selected components
    return selectedComponents.every(sc => checkCompatibility(sc, comp));
  });
};

export const getIncompatibleReasons = (component, selectedComponents) => {
  const reasons = [];
  for (const sc of selectedComponents) {
    if (!checkCompatibility(sc, component)) {
      reasons.push(`Incompatible with ${sc.name} due to conflicting requirements`);
    }
  }
  return reasons;
};